import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const STORAGE_KEY = "emera_cookie_consent";
const PREFS_KEY = "emera_cookie_prefs";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 12 months

type CategoryId = "necessarios" | "funcionais" | "analiticos" | "publicidade";

export type CookiePrefs = Record<CategoryId, boolean>;

const CATEGORIES: {
  id: CategoryId;
  label: string;
  description: string;
  locked?: boolean;
}[] = [
  {
    id: "necessarios",
    label: "Necessários",
    description:
      "Autenticação, segurança da sessão, memória do consentimento e prevenção a fraude. A Plataforma não funciona sem eles.",
    locked: true,
  },
  {
    id: "funcionais",
    label: "Funcionais / de preferências",
    description: "Idioma, região, preferências de exibição e retomada da aula no ponto em que você parou.",
  },
  {
    id: "analiticos",
    label: "Analíticos / de desempenho",
    description: "Métricas de audiência, funil e desempenho pedagógico.",
  },
  {
    id: "publicidade",
    label: "Publicidade e mensuração",
    description: "Remarketing, mensuração de conversão e públicos personalizados.",
  },
];

const ALL_OFF: CookiePrefs = { necessarios: true, funcionais: false, analiticos: false, publicidade: false };
const ALL_ON: CookiePrefs = { necessarios: true, funcionais: true, analiticos: true, publicidade: true };

const readConsent = (): string | null => {
  try {
    const ls = localStorage.getItem(STORAGE_KEY);
    if (ls) return ls;
  } catch {}
  const match = document.cookie.match(new RegExp(`(?:^|; )${STORAGE_KEY}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
};

export const readCookiePrefs = (): CookiePrefs | null => {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return { ...ALL_OFF, ...JSON.parse(raw) } as CookiePrefs;
  } catch {}
  const consent = readConsent();
  if (consent === "accepted") return ALL_ON;
  if (consent === "rejected") return ALL_OFF;
  return null;
};

const writeConsent = (prefs: CookiePrefs) => {
  const value = prefs.funcionais && prefs.analiticos && prefs.publicidade ? "accepted" : "rejected";
  try {
    localStorage.setItem(STORAGE_KEY, value);
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {}
  document.cookie = `${STORAGE_KEY}=${value}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
  window.dispatchEvent(new CustomEvent("emera:cookie-consent-updated", { detail: prefs }));
};

const CookieConsentBar = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const [prefs, setPrefs] = useState<CookiePrefs>(ALL_OFF);

  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    if (readConsent()) return;
    const t = setTimeout(() => setVisible(true), 400);
    return () => clearTimeout(t);
  }, [pathname]);

  useEffect(() => {
    const reopen = () => {
      setPrefs(readCookiePrefs() ?? ALL_OFF);
      setConfiguring(true);
      setLeaving(false);
      setVisible(true);
    };
    window.addEventListener("emera:open-cookie-preferences", reopen);
    return () => window.removeEventListener("emera:open-cookie-preferences", reopen);
  }, []);

  const close = () => {
    setLeaving(true);
    setTimeout(() => {
      setVisible(false);
      setConfiguring(false);
    }, 300);
  };

  const handleChoice = (next: CookiePrefs) => {
    writeConsent(next);
    close();
  };

  if (!visible) return null;

  const ghostBtn =
    "flex-1 md:flex-none px-5 py-2.5 rounded-md text-sm font-medium border transition-colors hover:bg-black/5";
  const solidBtn = "flex-1 md:flex-none px-5 py-2.5 rounded-md text-sm font-medium transition-colors hover:opacity-90";

  return (
    <div
      role="dialog"
      aria-label="Aviso de cookies"
      className={`fixed inset-x-0 bottom-0 z-[9999] border-t transition-all duration-300 ${
        leaving ? "translate-y-full opacity-0" : "translate-y-0 opacity-100"
      }`}
      style={{
        backgroundColor: "#F2F5FE",
        borderTopColor: "rgba(21,25,26,0.1)",
        paddingBottom: "max(10px, env(safe-area-inset-bottom))",
      }}
    >
      {configuring ? (
        <div className="container mx-auto px-5 py-4 max-h-[75vh] overflow-y-auto" style={{ color: "#15191A" }}>
          <p className="text-[15px] font-semibold mb-1">Preferências de cookies</p>
          <p className="text-[13px] leading-relaxed mb-4" style={{ color: "rgba(21,25,26,0.7)" }}>
            Escolha quais categorias deseja permitir. Cookies não necessários só são ativados após o seu consentimento.
            Saiba mais na{" "}
            <a href="/politica-de-privacidade" className="underline font-medium" style={{ color: "#5159D4" }}>
              Política de Privacidade
            </a>
            .
          </p>

          <div className="grid gap-3 md:grid-cols-2">
            {CATEGORIES.map((c) => (
              <label
                key={c.id}
                className={`flex gap-3 rounded-lg border p-3 ${c.locked ? "opacity-70" : "cursor-pointer"}`}
                style={{ borderColor: "rgba(21,25,26,0.15)" }}
              >
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 accent-[#5159D4]"
                  checked={c.locked ? true : prefs[c.id]}
                  disabled={c.locked}
                  onChange={(e) => setPrefs((p) => ({ ...p, [c.id]: e.target.checked }))}
                />
                <span>
                  <span className="block text-[14px] font-medium">
                    {c.label}
                    {c.locked && (
                      <span className="ml-2 text-[11px] uppercase tracking-wider" style={{ color: "rgba(21,25,26,0.5)" }}>
                        sempre ativos
                      </span>
                    )}
                  </span>
                  <span className="block text-[12px] leading-relaxed" style={{ color: "rgba(21,25,26,0.65)" }}>
                    {c.description}
                  </span>
                </span>
              </label>
            ))}
          </div>

          <div className="flex flex-col md:flex-row gap-3 mt-4 md:justify-end">
            <button
              type="button"
              onClick={() => handleChoice(ALL_OFF)}
              className={ghostBtn}
              style={{ borderColor: "rgba(21,25,26,0.25)", color: "#15191A", backgroundColor: "transparent" }}
            >
              Rejeitar todos
            </button>
            <button
              type="button"
              onClick={() => handleChoice(prefs)}
              className={ghostBtn}
              style={{ borderColor: "rgba(21,25,26,0.25)", color: "#15191A", backgroundColor: "transparent" }}
            >
              Salvar preferências
            </button>
            <button
              type="button"
              onClick={() => handleChoice(ALL_ON)}
              className={solidBtn}
              style={{ backgroundColor: "#15191A", color: "#FFFFFF" }}
            >
              Aceitar todos
            </button>
          </div>
        </div>
      ) : (
        <div className="container mx-auto px-5 py-[10px] flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
          <p className="text-[14px] leading-relaxed flex-1" style={{ color: "#15191A" }}>
            Utilizamos cookies para melhorar sua experiência. Ao continuar navegando, você concorda com nossa{" "}
            <a href="/politica-de-privacidade" className="underline font-medium" style={{ color: "#5159D4" }}>
              Política de Privacidade
            </a>
            .
          </p>
          <div className="flex flex-wrap gap-3 w-full md:w-auto">
            <button
              type="button"
              onClick={() => handleChoice(ALL_OFF)}
              className={ghostBtn}
              style={{ borderColor: "rgba(21,25,26,0.25)", color: "#15191A", backgroundColor: "transparent" }}
            >
              Recusar
            </button>
            <button
              type="button"
              onClick={() => {
                setPrefs(readCookiePrefs() ?? ALL_OFF);
                setConfiguring(true);
              }}
              className={ghostBtn}
              style={{ borderColor: "rgba(21,25,26,0.25)", color: "#15191A", backgroundColor: "transparent" }}
            >
              Configurar
            </button>
            <button
              type="button"
              onClick={() => handleChoice(ALL_ON)}
              className={solidBtn}
              style={{ backgroundColor: "#15191A", color: "#FFFFFF" }}
            >
              Aceitar cookies
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CookieConsentBar;
